<template>
  <div class="app-menu relative" ref="containerRef">
    <!-- 9-Dots Grid Toggle Button in Header -->
    <button
      id="tutorial-menu"
      class="menu-toggle"
      @click="toggleDropdown"
      :class="{ 'menu-toggle-active': dropdownOpen }"
      title="TheEar Suite Ecosystem"
      type="button"
      aria-label="TheEar Applications Launcher"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 28 28"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        class="fill-current transition-transform duration-200"
        :class="{ 'rotate-45 scale-105': dropdownOpen }"
      >
        <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
          <g
            transform="translate(-102.000000, -933.000000)"
            fill="currentColor"
          >
            <path
              d="M128,941 C128,942.104 127.104,943 126,943 L122,943 C120.896,943 120,942.104 120,941 L120,937 C120,935.896 120.896,935 122,935 L126,935 C127.104,935 128,935.896 128,937 L128,941 L128,941 Z M126,933 L122,933 C119.791,933 118,934.791 118,937 L118,941 C118,943.209 119.791,945 122,945 L126,945 C128.209,945 130,943.209 130,941 L130,937 C130,934.791 128.209,933 126,933 L126,933 Z M128,957 C128,958.104 127.104,959 126,959 L122,959 C120.896,959 120,958.104 120,957 L120,953 C120,951.896 120.896,951 122,951 L126,951 C127.104,951 128,951.896 128,953 L128,957 L128,957 Z M126,949 L122,949 C119.791,949 118,950.791 118,953 L118,957 C118,959.209 119.791,961 122,961 L126,961 C128.209,961 130,959.209 130,957 L130,953 C130,950.791 128.209,949 126,949 L126,949 Z M112,941 C112,942.104 111.104,943 110,943 L106,943 C104.896,943 104,942.104 104,941 L104,937 C104,935.896 104.896,935 106,935 L110,935 C111.104,935 112,935.896 112,937 L112,941 L112,941 Z M110,933 L106,933 C103.791,933 102,934.791 102,937 L102,941 C102,943.209 103.791,945 106,945 L110,945 C112.209,945 114,943.209 114,941 L114,937 C114,934.791 112.209,933 110,933 L110,933 Z M112,957 C112,958.104 111.104,959 110,959 L106,959 C104.896,959 104,958.104 104,957 L104,953 C104,951.896 104.896,951 106,951 L110,951 C111.104,951 112,951.896 112,953 L112,957 L112,957 Z M110,949 L106,949 C103.791,949 102,950.791 102,953 L102,957 C102,959.209 103.791,961 106,961 L110,961 C112.209,961 114,959.209 114,957 L114,953 C114,950.791 112.209,949 110,949 L110,949 Z"
            ></path>
          </g>
        </g>
      </svg>
    </button>

    <!-- Floating Dropdown Modal / Popup -->
    <Teleport to="body">
      <!-- Transparent Click-Outside Backdrop -->
      <div
        v-if="dropdownOpen"
        class="fixed inset-0 z-[9876542871] bg-transparent"
        @click="closeDropdown"
        id="utlity-menu-overlay"
      ></div>

      <!-- Popover Menu (Google Apps Launcher Style as in builderbo ProductsMenu) -->
      <transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0 translate-y-2 scale-95"
        enter-to-class="opacity-100 translate-y-0 scale-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100 translate-y-0 scale-100"
        leave-to-class="opacity-0 translate-y-2 scale-95"
      >
        <div
          v-if="dropdownOpen"
          class="ecosystem-popover fixed right-4 top-[74px] w-[310px] sm:w-[330px] max-w-[calc(100vw-32px)] z-[9876542873] bg-white border border-slate-200/90 rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.18)] p-3 flex flex-col gap-2 font-['Montserrat'] select-none overflow-visible animate-in fade-in text-slate-900"
          @click.stop
        >
          <!-- Top Context Header (Google Style Subtitle & Count) -->
          <div class="px-2 pt-0.5 flex items-center justify-between">
            <span
              class="ecosystem-title text-[11px] font-bold text-slate-900 truncate tracking-tight"
            >
              TheEar Suite Ecosystem
            </span>
            <span
              v-if="appsList.length > 0"
              class="app-count-badge text-[10px] font-mono font-semibold px-2 py-0.5 rounded-full"
            >
              {{ appsList.length }} Apps
            </span>
          </div>

          <!-- Inner Apps Container (Rounded Well) -->
          <div
            class="bg-slate-50/80 border border-slate-200/70 rounded-2xl p-2.5 max-h-[380px] overflow-y-auto custom-scrollbar overflow-visible relative"
          >
            <!-- Loading State -->
            <div
              v-if="isLoading && appsList.length === 0"
              class="py-10 flex flex-col items-center justify-center gap-2 text-slate-400 text-xs text-center"
            >
              <i class="fa-solid fa-spinner fa-spin text-base text-primary"></i>
              <span>Loading ecosystem apps...</span>
            </div>

            <!-- Empty State -->
            <div
              v-else-if="!isLoading && appsList.length === 0"
              class="py-8 flex flex-col items-center justify-center text-slate-400 text-xs gap-1.5 text-center"
            >
              <i
                class="fa-solid fa-layer-group text-2xl text-slate-300 mb-1"
              ></i>
              <span class="font-medium text-slate-700">No apps available</span>
              <span class="text-[11px] text-slate-400"
                >Unable to connect to TheEar suite</span
              >
            </div>

            <!-- 3-Columns Grid (Exact Google Waffle App Grid with Hover Tooltip) -->
            <div v-else class="grid grid-cols-3 gap-y-1.5 gap-x-1">
              <div
                v-for="(product, idx) in appsList"
                :key="product.id || product.label || idx"
                class="relative flex flex-col items-center group w-full"
              >
                <!-- Floating Tooltip on Hover showing full product name (Google App Launcher Style) -->
                <div
                  class="pointer-events-none absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full z-50 opacity-0 group-hover:opacity-100 transition-all duration-150 scale-95 group-hover:scale-100 bg-slate-900 text-white text-[11px] font-bold px-2.5 py-1 rounded-lg shadow-xl whitespace-nowrap flex items-center justify-center"
                >
                  <span class="text-white">{{ product.label }}</span>
                  <div
                    class="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900"
                  ></div>
                </div>

                <!-- Enabled Product Card (Links to Backoffice) -->
                <a
                  v-if="product.is_enabled"
                  :href="product.link || 'javascript:void(0)'"
                  :target="product.link ? '_blank' : '_self'"
                  :rel="product.link ? 'noopener noreferrer' : undefined"
                  class="app-card w-full flex flex-col items-center justify-start p-1.5 rounded-xl hover:bg-slate-200/50 active:scale-95 transition-all duration-150 cursor-pointer text-center no-underline select-none min-h-[74px]"
                  @click="handleProductClick(product, $event)"
                >
                  <!-- App Icon Container (Vibrant App Badge with site primary color) -->
                  <div
                    class="app-icon-badge w-11 h-11 rounded-xl flex items-center justify-center p-2 shadow-xs transition-transform duration-200 group-hover:scale-110 relative"
                  >
                    <!-- Image Icon -->
                    <img
                      v-if="
                        product.icon &&
                        typeof product.icon === 'string' &&
                        (product.icon.startsWith('http') ||
                          product.icon.startsWith('/') ||
                          product.icon.startsWith('data:')) &&
                        !failedIcons[product.id]
                      "
                      :src="product.icon"
                      :alt="product.label"
                      class="w-full h-full object-contain filter drop-shadow-sm"
                      loading="lazy"
                      @error="handleImageError(product.id)"
                    />
                    <!-- Fallback Icon -->
                    <i
                      v-else
                      :class="[
                        getFallbackIcon(product),
                        'text-lg transition-colors text-white',
                      ]"
                    ></i>
                  </div>

                  <!-- App Label Below Icon (Black Text) -->
                  <div
                    class="mt-1.5 w-full flex items-start justify-center min-h-[28px] px-0.5 pointer-events-none"
                  >
                    <span
                      class="app-label text-[11px] font-semibold leading-tight text-center transition-colors duration-150 line-clamp-1 group-hover:line-clamp-none group-hover:whitespace-normal group-hover:break-words max-w-[85px] text-slate-900"
                    >
                      {{ product.label }}
                    </span>
                  </div>
                </a>

                <!-- Disabled Product Card (Disabled Visuals + Discover/Info Link) -->
                <div
                  v-else
                  class="w-full flex flex-col items-center justify-start p-1.5 rounded-xl transition-all duration-150 text-center select-none min-h-[74px]"
                >
                  <!-- App Icon Container (Disabled / Opacity) with Lock Badge -->
                  <div
                    class="app-icon-badge w-11 h-11 rounded-xl flex items-center justify-center p-2 shadow-xs transition-transform duration-200 relative opacity-50 grayscale-[35%] group-hover:opacity-75 group-hover:grayscale-0 cursor-not-allowed"
                  >
                    <!-- Lock Indicator Badge -->
                    <div
                      class="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-slate-800 text-amber-400 border border-slate-600/40 flex items-center justify-center shadow-xs"
                    >
                      <i class="fa-solid fa-lock text-[8px]"></i>
                    </div>

                    <!-- Image Icon -->
                    <img
                      v-if="
                        product.icon &&
                        typeof product.icon === 'string' &&
                        (product.icon.startsWith('http') ||
                          product.icon.startsWith('/') ||
                          product.icon.startsWith('data:')) &&
                        !failedIcons[product.id]
                      "
                      :src="product.icon"
                      :alt="product.label"
                      class="w-full h-full object-contain filter drop-shadow-sm"
                      loading="lazy"
                      @error="handleImageError(product.id)"
                    />
                    <i
                      v-else
                      :class="[getFallbackIcon(product), 'text-lg text-white']"
                    ></i>
                  </div>

                  <!-- App Label Below Icon -->
                  <div
                    class="mt-1.5 w-full flex items-start justify-center min-h-[28px] px-0.5 opacity-75 cursor-default"
                  >
                    <span
                      class="app-label text-[11px] font-semibold leading-tight text-center line-clamp-1 max-w-[85px] text-slate-900"
                    >
                      {{ product.label }}
                    </span>
                  </div>

                  <!-- Discover Button if linkdetail exists -->
                  <a
                    v-if="product.linkdetail"
                    :href="product.linkdetail"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="mt-0.5 inline-flex items-center justify-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold text-sky-700 bg-sky-100/70 hover:bg-sky-200/70 border border-sky-300/80 shadow-2xs hover:shadow-xs transition-all duration-150 active:scale-95 group/link cursor-pointer max-w-full text-center no-underline"
                    @click.stop="closeDropdown"
                  >
                    <span>Discover</span>
                    <i
                      class="fa-solid fa-arrow-up-right-from-square text-[7.5px] text-sky-600"
                    ></i>
                  </a>
                  <span
                    v-else
                    class="mt-0.5 inline-flex items-center justify-center gap-1 px-1.5 py-0.5 rounded-full text-[8.5px] font-medium text-slate-400 bg-slate-100/80 border border-slate-200/80 select-none cursor-default"
                  >
                    <i class="fa-solid fa-lock text-[7px] text-slate-400"></i>
                    <span>Not active</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Bottom Footer Action (Google Style 'More from TheEar' Link) -->
          <div
            class="px-2 pt-1 pb-0.5 flex items-center justify-center border-t border-slate-100 mt-0.5"
          >
            <a
              :href="allProductsUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="primary-footer-link inline-flex items-center gap-1.5 text-[11px] font-semibold transition-colors no-underline cursor-pointer"
              @click="closeDropdown"
            >
              <span>More from TheEar</span>
              <i class="fa-solid fa-arrow-up-right-from-square text-[9px]"></i>
            </a>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script>
import { getFetchMS } from "@/api/axiosClient";

const PRODUCT_METADATA = {
  builder: {
    label: "Builder",
    category: "Game Studio",
    iconClass: "fa-solid fa-cubes",
  },
  "crash game": {
    label: "Crash Game",
    category: "Game Suite",
    iconClass: "fa-solid fa-plane-departure",
  },
  crashgames: {
    label: "Crash Game",
    category: "Game Suite",
    iconClass: "fa-solid fa-plane-departure",
  },
  platform: {
    label: "Platform",
    category: "Core Engine",
    iconClass: "fa-solid fa-link",
  },
  "platform & crm": {
    label: "Platform & CRM",
    category: "Core Engine",
    iconClass: "fa-solid fa-link",
  },
  analytics: {
    label: "Analytics",
    category: "BI & Data",
    iconClass: "fa-solid fa-chart-simple",
  },
  crm: {
    label: "CRM",
    category: "Backoffice",
    iconClass: "fa-solid fa-building",
  },
  "crm italy": {
    label: "CRM Italy",
    category: "Compliance IT",
    iconClass: "fa-solid fa-building",
  },
  "the ear casino": {
    label: "Casino",
    category: "B2C Portal",
    iconClass: "fa-solid fa-gamepad",
  },
  "the ear slots": {
    label: "The Ear Slots",
    category: "Game Suite",
    iconClass: "fa-solid fa-dice",
  },
  theearslots: {
    label: "The Ear Slots",
    category: "Game Suite",
    iconClass: "fa-solid fa-dice",
  },
  aggregator: {
    label: "Aggregator Providers",
    category: "Game Aggregation",
    iconClass: "fa-solid fa-rss",
  },
  "aggregator pro": {
    label: "Aggregator Pro",
    category: "Aggregation",
    iconClass: "fa-solid fa-rss",
  },
};

const DEFAULT_APPS = [
  {
    id: "builder",
    label: "Builder",
    category: "Game Studio",
    is_enabled: true,
    linkdetail: "https://www.theearcasino.com",
    link: "https://builder.theearplatform.com/verifytoken/eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJUaGVFYXIiLCJzdWIiOjEsInVzZXJfaWQiOjEsImlzX2FkbWluIjoxLCJkZXRhaWwiOnsibmFtZSI6IlN5c3RlbSBBZG1pbiIsImVtYWlsIjoiYWRtaW5AdGhlZWFycGxhdGZvcm0uY29tIiwidGh1bWIiOiJ1c2Vycy90aHVtYnMvVnU1c0FHSk53dU1hdHBZZ0Q2aEJ4Z1pJdk15c21xWTN3cVFQaGl2Wi5qcGciLCJjcmVhdGVkX2F0IjoiMjAxOS0wOC0wOFQxMjo0MzoyMS4wMDAwMDBaIiwidXBkYXRlZF9hdCI6IjIwMjYtMDgtMjdUMDk6MDQ6NTEuMDAwMDAwWiJ9LCJza2lucyI6W10sInByb3ZpZGVycyI6W10sImFwcHMiOltdLCJpYXQiOjE3ODk2NDA1MjIsImV4cCI6MTc4OTcyNjkyMn0.Bosc82X3YVKZ506xDVO8hnMasm06kCSNAWleC5tDjrE",
    icon: "https://storage.googleapis.com/camasy-images/apps/builder.svg",
    description: "Slot & Game Engine Studio",
  },
  {
    id: "crashgames",
    label: "Crash Game",
    category: "Game Suite",
    is_enabled: true,
    linkdetail: "https://www.theearcasino.com",
    link: "https://crashgames.theearplatform.com/verifytoken/eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJUaGVFYXIiLCJzdWIiOjEsInVzZXJfaWQiOjEsImlzX2FkbWluIjoxLCJkZXRhaWwiOnsibmFtZSI6IlN5c3RlbSBBZG1pbiIsImVtYWlsIjoiYWRtaW5AdGhlZWFycGxhdGZvcm0uY29tIiwidGh1bWIiOiJ1c2Vycy90aHVtYnMvVnU1c0FHSk53dU1hdHBZZ0Q2aEJ4Z1pJdk15c21xWTN3cVFQaGl2Wi5qcGciLCJjcmVhdGVkX2F0IjoiMjAxOS0wOC0wOFQxMjo0MzoyMS4wMDAwMDBaIiwidXBkYXRlZF9hdCI6IjIwMjYtMDgtMjdUMDk6MDQ6NTEuMDAwMDAwWiJ9LCJza2lucyI6W10sInByb3ZpZGVycyI6W10sImFwcHMiOltdLCJpYXQiOjE3ODk2NDA1MjIsImV4cCI6MTc4OTcyNjkyMn0.Bosc82X3YVKZ506xDVO8hnMasm06kCSNAWleC5tDjrE",
    icon: "https://storage.googleapis.com/camasy-images/apps/crash.svg",
    description: "Crash Management, Rains, Challenges",
  },
  {
    id: "platform",
    label: "Platform & CRM",
    category: "Core PAM & CRM",
    is_enabled: true,
    linkdetail: "https://www.theearcasino.com",
    link: "https://bo.theearplatform.com/",
    icon: "https://storage.googleapis.com/camasy-images/apps/platform.svg",
    description:
      "Player Account Management, Deposits, Cashier & Integrated CRM",
  },
  {
    id: "analytics",
    label: "Analytics",
    category: "BI & Data",
    is_enabled: true,
    linkdetail: "https://www.theearcasino.com",
    link: "https://analytics.theearplatform.com/verifytoken/eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJUaGVFYXIiLCJzdWIiOjEsInVzZXJfaWQiOjEsImlzX2FkbWluIjoxLCJkZXRhaWwiOnsibmFtZSI6IlN5c3RlbSBBZG1pbiIsImVtYWlsIjoiYWRtaW5AdGhlZWFycGxhdGZvcm0uY29tIiwidGh1bWIiOiJ1c2Vycy90aHVtYnMvVnU1c0FHSk53dU1hdHBZZ0Q2aEJ4Z1pJdk15c21xWTN3cVFQaGl2Wi5qcGciLCJjcmVhdGVkX2F0IjoiMjAxOS0wOC0wOFQxMjo0MzoyMS4wMDAwMDBaIiwidXBkYXRlZF9hdCI6IjIwMjYtMDgtMjdUMDk6MDQ6NTEuMDAwMDAwWiJ9LCJza2lucyI6W10sInByb3ZpZGVycyI6W10sImFwcHMiOltdLCJpYXQiOjE3ODk2NDA1MjIsImV4cCI6MTc4OTcyNjkyMn0.Bosc82X3YVKZ506xDVO8hnMasm06kCSNAWleC5tDjrE",
    icon: "https://storage.googleapis.com/camasy-images/apps/analytics.svg",
    description: "Real-Time KPIs & Telemetry",
  },
  {
    id: "aggregator",
    label: "Aggregator Providers",
    category: "Game Aggregation",
    is_enabled: true,
    linkdetail: "https://www.theearcasino.com",
    link: "https://www.theearcasino.com",
    icon: "https://storage.googleapis.com/camasy-images/apps/aggregator.svg",
    description: "Multi-Provider Integration & Certified Game Catalog",
  },
  {
    id: "theearslots",
    label: "The Ear Slots",
    category: "Exclusive Games",
    is_enabled: true,
    link: "https://www.theearcasino.com",
    linkdetail: "https://www.theearcasino.com",
    icon: "https://storage.googleapis.com/camasy-images/apps/theearslot.svg",
    description: "Proprietary Slot Portfolio & In-House Content",
  },
];

export default {
  name: "UtlitiesMenuApps",
  data() {
    return {
      dropdownOpen: false,
      isLoading: false,
      isFirstFetch: true,
      appsList: DEFAULT_APPS,
      failedIcons: {},
      allProductsUrl: "https://www.theearcasino.com",
    };
  },
  watch: {
    dropdownOpen(val) {
      if (val && this.isFirstFetch) {
        this.fetchApps();
      }
    },
  },
  methods: {
    formatApiProduct(product) {
      const key = String(product.id || product.label || "")
        .trim()
        .toLowerCase();
      const meta = PRODUCT_METADATA[key] || {};
      const isEnabled =
        product.is_enabled !== false &&
        product.is_enabled !== 0 &&
        product.is_enabled !== "0" &&
        product.is_enabled !== "false";

      return {
        ...product,
        id: product.id || key,
        is_enabled: isEnabled,
        label: meta.label || product.label,
        category: product.category || meta.category || "Product Suite",
        iconClass: meta.iconClass || "fa-solid fa-layer-group",
        icon: product.icon || "",
        description: product.description || meta.description || "",
        link: isEnabled ? product.link || product.url || null : null,
        linkdetail:
          product.linkdetail ||
          product.link_detail ||
          product.linkDetail ||
          "https://www.theearcasino.com",
      };
    },
    async fetchApps() {
      this.isLoading = true;
      try {
        const url =
          process.env.VUE_APP_API_MS +
          "/static/theearuser/apps?module=theearcasino";
        const res = await getFetchMS(url);

        let rawList = [];
        if (Array.isArray(res)) {
          rawList = res;
        } else if (Array.isArray(res?.data)) {
          rawList = res.data;
        } else if (Array.isArray(res?.apps)) {
          rawList = res.apps;
        } else if (Array.isArray(res?.data?.apps)) {
          rawList = res.data.apps;
        } else if (Array.isArray(res?.data?.items)) {
          rawList = res.data.items;
        } else if (res && typeof res === "object") {
          const found = Object.values(res?.data ?? res).find((val) =>
            Array.isArray(val),
          );
          if (found) rawList = found;
        }

        if (rawList && rawList.length > 0) {
          this.appsList = rawList
            .filter((p) => {
              const id = String(p.id || "").toLowerCase();
              const label = String(p.label || "").toLowerCase();
              return (
                !p.is_current &&
                id !== "theearcasino" &&
                label !== "the ear casino" &&
                id !== "promotool" &&
                id !== "promotol" &&
                !id.includes("promo") &&
                !label.includes("promo")
              );
            })
            .map(this.formatApiProduct);
        } else {
          this.appsList = DEFAULT_APPS.map(this.formatApiProduct);
        }
      } catch (err) {
        console.warn("Errore fetch apps:", err);
        this.appsList = DEFAULT_APPS.map(this.formatApiProduct);
      } finally {
        this.isLoading = false;
        this.isFirstFetch = false;
      }
    },
    getFallbackIcon(app) {
      const key = String(app.id || app.label || "")
        .trim()
        .toLowerCase();
      const meta = PRODUCT_METADATA[key];
      if (meta && meta.iconClass) return meta.iconClass;
      return "fa-solid fa-layer-group";
    },
    handleImageError(appId) {
      if (appId) {
        this.failedIcons[appId] = true;
      }
    },
    getProductUrl(product) {
      if (!product) return null;
      return product.is_enabled
        ? product.link || product.linkdetail || null
        : product.linkdetail || null;
    },
    handleProductClick(product, event) {
      const url = this.getProductUrl(product);
      if (!url) {
        if (event) {
          event.preventDefault();
          event.stopPropagation();
        }
        return;
      }
      this.closeDropdown();
      this.$emit("select", product);
    },
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen;
      if (this.dropdownOpen && this.isFirstFetch) {
        this.fetchApps();
      }
    },
    closeDropdown() {
      this.dropdownOpen = false;
    },
    handleEscKey(event) {
      if (event.key === "Escape" && this.dropdownOpen) {
        this.closeDropdown();
      }
    },
  },
  mounted() {
    this.appsList = DEFAULT_APPS.map(this.formatApiProduct);
    this.fetchApps();
    document.addEventListener("keydown", this.handleEscKey);
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.handleEscKey);
  },
};
</script>

<style scoped>
.app-menu {
  position: relative;
  font-family: "Montserrat", sans-serif !important;
}

.menu-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  width: 40px;
  height: 40px;
  color: #334155;
  transition: all 0.2s ease;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.menu-toggle:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #0f172a;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.menu-toggle-active {
  background: var(--primary-color, #386f99) !important;
  border-color: var(--primary-color, #386f99) !important;
  color: #ffffff !important;
  box-shadow: 0 2px 8px rgba(56, 111, 153, 0.35) !important;
}

.app-icon-badge {
  background: var(--primary-color, #386f99);
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.app-count-badge {
  background: var(--primary-light, rgba(56, 111, 153, 0.1));
  color: var(--primary-color, #386f99);
  border: 1px solid var(--primary-border, rgba(56, 111, 153, 0.3));
}

.primary-footer-link {
  color: var(--primary-color, #386f99);
}

.primary-footer-link:hover {
  color: var(--primary-hover, #32648a);
}

.text-primary {
  color: var(--primary-color, #386f99);
}

.ecosystem-title,
.app-label,
.app-card,
.app-card span {
  color: #0f172a !important;
}

/* Custom Scrollbar for app container */
.custom-scrollbar::-webkit-scrollbar {
  width: 5px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 9999px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
